import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from "../ui/sheet";
import { toast } from "sonner";
import { formatCurrency, formatDate } from "../../lib/propiedadUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });
const hoy = () => new Date().toISOString().split("T")[0];

// ---------- Registrar pago (panel lateral) ----------
export const RegistrarPagoSheet = ({ open, onOpenChange, propiedad, onSuccess }) => {
  const [cargos, setCargos] = useState([]);
  const [cargoId, setCargoId] = useState("");
  const [form, setForm] = useState({ fecha_pago: hoy(), importe: "", metodo_pago: "Transferencia", referencia: "", nota: "" });
  const [comprobante, setComprobante] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ fecha_pago: hoy(), importe: "", metodo_pago: "Transferencia", referencia: "", nota: "" });
      setComprobante(null);
      axios
        .get(`${API}/propiedades/${propiedad.id}/cargos-pendientes`, { headers: authHeader() })
        .then((r) => {
          setCargos(r.data);
          if (r.data.length > 0) {
            setCargoId(r.data[0].id);
            setForm((f) => ({ ...f, importe: String(r.data[0].saldo_pendiente) }));
          } else {
            setCargoId("");
          }
        })
        .catch(() => toast.error("Error al cargar los cargos pendientes"));
    }
  }, [open, propiedad.id]);

  const cargo = cargos.find((c) => c.id === cargoId);
  const monto = parseFloat(form.importe) || 0;
  const saldoRestante = cargo ? Math.max(cargo.saldo_pendiente - monto, 0) : 0;
  const esRenta = cargo?.tipo === "Renta mensual";
  const pct = propiedad.comision_porcentaje;
  const comisionEstimada = esRenta && pct ? Math.round(monto * pct) / 100 : 0;
  const netoPropietario = Math.round((monto - comisionEstimada) * 100) / 100;

  const handleSubmit = async () => {
    if (!cargo) return toast.error("Selecciona un cargo");
    if (monto <= 0) return toast.error("El monto debe ser mayor a 0");
    if (monto > cargo.saldo_pendiente + 0.01) return toast.error(`El monto no puede exceder el saldo (${formatCurrency(cargo.saldo_pendiente)})`);
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("cargo_id", cargo.id);
      fd.append("fecha_pago", form.fecha_pago);
      fd.append("importe", monto);
      fd.append("metodo_pago", form.metodo_pago);
      if (form.referencia) fd.append("referencia", form.referencia);
      if (form.nota) fd.append("nota", form.nota);
      if (comprobante) fd.append("comprobante", comprobante);
      const r = await axios.post(`${API}/cobranza/pagos`, fd, { headers: authHeader() });
      toast.success(
        r.data.comision_generada
          ? `Pago registrado. Comisión de ${formatCurrency(r.data.comision_generada.monto)} generada automáticamente`
          : "Pago registrado exitosamente"
      );
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar el pago");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="registrar-pago-sheet">
        <SheetHeader>
          <SheetTitle>Registrar pago · {propiedad.nombre_corto}</SheetTitle>
        </SheetHeader>
        {cargos.length === 0 ? (
          <p className="text-sm text-gray-500 py-6">No hay cargos pendientes para esta propiedad.</p>
        ) : (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Cargo a pagar</Label>
              <Select
                value={cargoId}
                onValueChange={(v) => {
                  setCargoId(v);
                  const c = cargos.find((x) => x.id === v);
                  if (c) setForm((f) => ({ ...f, importe: String(c.saldo_pendiente) }));
                }}
              >
                <SelectTrigger data-testid="pago-cargo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {cargos.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.descripcion} · vence {formatDate(c.fecha_vencimiento)} · saldo {formatCurrency(c.saldo_pendiente)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Fecha del pago</Label>
                <Input type="date" value={form.fecha_pago} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} data-testid="pago-fecha-input" />
              </div>
              <div className="space-y-2">
                <Label>Monto</Label>
                <Input type="number" step="0.01" min="0" value={form.importe} onChange={(e) => setForm({ ...form, importe: e.target.value })} data-testid="pago-monto-input" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Método de pago</Label>
              <Select value={form.metodo_pago} onValueChange={(v) => setForm({ ...form, metodo_pago: v })}>
                <SelectTrigger data-testid="pago-metodo-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Transferencia">Transferencia</SelectItem>
                  <SelectItem value="Efectivo">Efectivo</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                  <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                  <SelectItem value="Depósito">Depósito</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Referencia (opcional)</Label>
              <Input value={form.referencia} onChange={(e) => setForm({ ...form, referencia: e.target.value })} data-testid="pago-referencia-input" />
            </div>
            <div className="space-y-2">
              <Label>Comprobante (opcional)</Label>
              <Input type="file" accept=".pdf,.png,.jpg,.jpeg" onChange={(e) => setComprobante(e.target.files?.[0] || null)} data-testid="pago-comprobante-input" />
            </div>
            <div className="space-y-2">
              <Label>Observaciones (opcional)</Label>
              <Textarea rows={2} value={form.nota} onChange={(e) => setForm({ ...form, nota: e.target.value })} data-testid="pago-nota-input" />
            </div>

            {/* Cálculos automáticos */}
            <div className="rounded-lg bg-gray-50 border border-gray-100 p-3 space-y-1.5 text-sm" data-testid="pago-calculos">
              <div className="flex justify-between"><span className="text-gray-500">Saldo restante del cargo</span><strong className={saldoRestante > 0 ? "text-amber-600" : "text-emerald-600"}>{formatCurrency(saldoRestante)}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500">Estado resultante</span><strong>{saldoRestante <= 0.01 ? "Pagado" : "Parcial"}</strong></div>
              {esRenta && pct ? (
                <>
                  <div className="flex justify-between"><span className="text-gray-500">Comisión admón. ({pct}%)</span><strong>{formatCurrency(comisionEstimada)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Neto para el propietario</span><strong className="text-emerald-700">{formatCurrency(netoPropietario)}</strong></div>
                  <p className="text-xs text-gray-400 pt-1">La comisión se registrará automáticamente como gasto de administración.</p>
                </>
              ) : esRenta ? (
                <p className="text-xs text-amber-600 pt-1">Sin % de comisión configurado: no se generará comisión automática.</p>
              ) : null}
            </div>
          </div>
        )}
        <SheetFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving || cargos.length === 0} data-testid="pago-guardar-btn">
            {saving ? "Registrando..." : "Registrar pago"}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

// ---------- Agregar gasto ----------
export const AgregarGastoDialog = ({ open, onOpenChange, propiedad, onSuccess }) => {
  const [categorias, setCategorias] = useState([]);
  const [form, setForm] = useState({ concepto: "", monto: "", categoria_id: "", fecha_gasto: hoy(), descripcion: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({ concepto: "", monto: "", categoria_id: "", fecha_gasto: hoy(), descripcion: "" });
      axios
        .get(`${API}/gastos/categorias`, { headers: authHeader() })
        .then((r) => setCategorias(r.data))
        .catch(() => toast.error("Error al cargar categorías"));
    }
  }, [open]);

  const handleSubmit = async () => {
    if (!form.concepto || !form.monto || !form.categoria_id) return toast.error("Concepto, monto y categoría son requeridos");
    setSaving(true);
    try {
      await axios.post(
        `${API}/gastos`,
        {
          concepto: form.concepto,
          monto: parseFloat(form.monto),
          descripcion: form.descripcion || null,
          categoria_id: form.categoria_id,
          ligado_a: "contrato",
          contrato_id: propiedad.contrato_vigente_id,
          tipo_gasto: "unico",
          fecha_gasto: form.fecha_gasto,
        },
        { headers: authHeader() }
      );
      toast.success("Gasto registrado");
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al registrar el gasto");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="agregar-gasto-modal">
        <DialogHeader><DialogTitle>Agregar gasto · {propiedad.nombre_corto}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Concepto *</Label>
            <Input value={form.concepto} onChange={(e) => setForm({ ...form, concepto: e.target.value })} data-testid="gasto-concepto-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Monto *</Label>
              <Input type="number" step="0.01" min="0" value={form.monto} onChange={(e) => setForm({ ...form, monto: e.target.value })} data-testid="gasto-monto-input" />
            </div>
            <div className="space-y-2">
              <Label>Fecha</Label>
              <Input type="date" value={form.fecha_gasto} onChange={(e) => setForm({ ...form, fecha_gasto: e.target.value })} data-testid="gasto-fecha-input" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Categoría *</Label>
            <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
              <SelectTrigger data-testid="gasto-categoria-select"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
              <SelectContent>
                {categorias.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Descripción (opcional)</Label>
            <Textarea rows={2} value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="gasto-guardar-btn">{saving ? "Guardando..." : "Guardar gasto"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Subir documento ----------
export const SubirDocumentoDialog = ({ open, onOpenChange, propiedad, onSuccess }) => {
  const [titulo, setTitulo] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTitulo(""); setArchivo(null); }
  }, [open]);

  const handleSubmit = async () => {
    if (!titulo || !archivo) return toast.error("Título y archivo son requeridos");
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("titulo", titulo);
      fd.append("file", archivo);
      await axios.post(`${API}/contracts/${propiedad.contrato_vigente_id}/upload`, fd, { headers: authHeader() });
      toast.success("Documento subido al expediente");
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al subir el documento");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="subir-documento-modal">
        <DialogHeader><DialogTitle>Subir documento · {propiedad.nombre_corto}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Título del documento *</Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} placeholder="Ej: Identificación del inquilino" data-testid="documento-titulo-input" />
          </div>
          <div className="space-y-2">
            <Label>Archivo *</Label>
            <Input type="file" onChange={(e) => setArchivo(e.target.files?.[0] || null)} data-testid="documento-archivo-input" />
            <p className="text-xs text-gray-500">Se asociará al contrato vigente {propiedad.numero_contrato}</p>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="documento-guardar-btn">{saving ? "Subiendo..." : "Subir documento"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Agregar nota ----------
export const AgregarNotaDialog = ({ open, onOpenChange, propiedad, onSuccess }) => {
  const [texto, setTexto] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setTexto(""); }, [open]);

  const handleSubmit = async () => {
    if (!texto.trim()) return toast.error("Escribe la nota");
    setSaving(true);
    try {
      await axios.post(`${API}/propiedades/${propiedad.id}/notas`, { texto }, { headers: authHeader() });
      toast.success("Nota agregada al historial");
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al guardar la nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="agregar-nota-modal">
        <DialogHeader><DialogTitle>Agregar nota interna</DialogTitle></DialogHeader>
        <Textarea rows={4} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Ej: Se llamó al inquilino para recordar el pago..." data-testid="nota-texto-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="nota-guardar-btn">{saving ? "Guardando..." : "Guardar nota"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Terminar contrato (desocupar propiedad) ----------
export const TerminarContratoDialog = ({ open, onOpenChange, contratoId, contratoNumero, onSuccess }) => {
  const [form, setForm] = useState({ fecha_desocupacion: hoy(), motivo: "", cancelar_cargos_pendientes: true });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setForm({ fecha_desocupacion: hoy(), motivo: "", cancelar_cargos_pendientes: true });
  }, [open]);

  const handleSubmit = async () => {
    if (!form.fecha_desocupacion) return toast.error("La fecha de desocupación es requerida");
    setSaving(true);
    try {
      const r = await axios.post(
        `${API}/contracts/${contratoId}/terminar`,
        form,
        { headers: authHeader() }
      );
      toast.success(
        r.data.cargos_cancelados > 0
          ? `Contrato terminado. ${r.data.cargos_cancelados} cargos futuros cancelados. La propiedad queda disponible.`
          : "Contrato terminado. La propiedad queda disponible."
      );
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al terminar el contrato");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="terminar-contrato-modal">
        <DialogHeader><DialogTitle>Terminar contrato {contratoNumero}</DialogTitle></DialogHeader>
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
          Usa esta acción cuando el contrato <strong>no se renovó</strong> y el inquilino desocupa la propiedad.
          El contrato quedará marcado como <strong>Terminado</strong> y la propiedad como <strong>Disponible</strong>.
        </div>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Fecha de desocupación *</Label>
            <Input type="date" value={form.fecha_desocupacion} onChange={(e) => setForm({ ...form, fecha_desocupacion: e.target.value })} data-testid="terminar-fecha-input" />
          </div>
          <div className="space-y-2">
            <Label>Motivo (opcional)</Label>
            <Textarea rows={2} value={form.motivo} onChange={(e) => setForm({ ...form, motivo: e.target.value })} placeholder="Ej: El inquilino no renovó, cambio de ciudad..." data-testid="terminar-motivo-input" />
          </div>
          <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
            <input
              type="checkbox"
              checked={form.cancelar_cargos_pendientes}
              onChange={(e) => setForm({ ...form, cancelar_cargos_pendientes: e.target.checked })}
              className="mt-0.5"
              data-testid="terminar-cancelar-cargos-checkbox"
            />
            <span>Cancelar los cargos de renta futuros sin pagos (posteriores a la fecha de desocupación)</span>
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button variant="destructive" onClick={handleSubmit} disabled={saving} data-testid="terminar-confirmar-btn">
            {saving ? "Terminando..." : "Terminar contrato"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---------- Renovar contrato ----------
export const RenovarContratoDialog = ({ open, onOpenChange, propiedad, onSuccess }) => {
  const [form, setForm] = useState({ fecha_inicio: "", fecha_termino: "", renta_mensual: "", deposito_garantia: "", observaciones: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const base = propiedad.fecha_termino_contrato ? new Date(propiedad.fecha_termino_contrato + "T00:00:00") : new Date();
      const inicio = new Date(base);
      inicio.setDate(inicio.getDate() + 1);
      const termino = new Date(inicio);
      termino.setFullYear(termino.getFullYear() + 1);
      termino.setDate(termino.getDate() - 1);
      setForm({
        fecha_inicio: inicio.toISOString().split("T")[0],
        fecha_termino: termino.toISOString().split("T")[0],
        renta_mensual: propiedad.renta_mensual != null ? String(propiedad.renta_mensual) : "",
        deposito_garantia: propiedad.deposito_garantia != null ? String(propiedad.deposito_garantia) : "",
        observaciones: "",
      });
    }
  }, [open, propiedad]);

  const handleSubmit = async () => {
    if (!form.fecha_inicio || !form.fecha_termino) return toast.error("Las fechas son requeridas");
    setSaving(true);
    try {
      await axios.post(
        `${API}/contracts/${propiedad.contrato_vigente_id}/renew`,
        {
          fecha_inicio: form.fecha_inicio,
          fecha_termino: form.fecha_termino,
          renta_mensual: form.renta_mensual ? parseFloat(form.renta_mensual) : null,
          deposito_garantia: form.deposito_garantia ? parseFloat(form.deposito_garantia) : null,
          observaciones: form.observaciones || null,
        },
        { headers: authHeader() }
      );
      toast.success("Contrato renovado exitosamente");
      onOpenChange(false);
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al renovar el contrato");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="renovar-contrato-modal">
        <DialogHeader><DialogTitle>Renovar contrato {propiedad.numero_contrato}</DialogTitle></DialogHeader>
        <p className="text-sm text-gray-500">
          Se copiarán automáticamente los datos del contrato anterior (propietario, inquilino, inmueble, comisión) y se generarán los nuevos cargos de renta.
        </p>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fecha de inicio *</Label>
              <Input type="date" value={form.fecha_inicio} onChange={(e) => setForm({ ...form, fecha_inicio: e.target.value })} data-testid="renovar-inicio-input" />
            </div>
            <div className="space-y-2">
              <Label>Fecha de término *</Label>
              <Input type="date" value={form.fecha_termino} onChange={(e) => setForm({ ...form, fecha_termino: e.target.value })} data-testid="renovar-termino-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Nueva renta mensual</Label>
              <Input type="number" step="0.01" value={form.renta_mensual} onChange={(e) => setForm({ ...form, renta_mensual: e.target.value })} data-testid="renovar-renta-input" />
            </div>
            <div className="space-y-2">
              <Label>Depósito en garantía</Label>
              <Input type="number" step="0.01" value={form.deposito_garantia} onChange={(e) => setForm({ ...form, deposito_garantia: e.target.value })} />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Observaciones</Label>
            <Textarea rows={2} value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={saving} data-testid="renovar-guardar-btn">{saving ? "Renovando..." : "Renovar contrato"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
